/**
 * The void row's QuickBooks copy: what the operator reads, and whether
 * the row is allowed to go quiet.
 *
 * Olive's push into QuickBooks is CREATE-ONLY. It never voids, updates or
 * deletes anything there. So an invoice voided in Olive can still have a
 * live copy at Intuit taking a family's money, while Olive frees that
 * invoice's events to be re-billed onto a new one. The per-invoice check
 * asks QuickBooks about that copy; migration 0171 stores the answer; this
 * module is the whole surface between the stored answer and a person.
 *
 * TWO LAWS, and neither is decoration.
 *
 * 1. EVERY STATE NAMES THE ACT THAT CLEARS IT AND THE SYSTEM IT HAPPENS
 *    IN. A status that does not say where to go sends the operator
 *    hunting across two systems at month end.
 *
 * 2. NO CLAIM IS PRESENT TENSE. Olive knows what QuickBooks looked like
 *    WHEN IT LAST LOOKED, which is a different sentence from "the copy is
 *    open". Every line here is past tense with an as-of and an
 *    imperative, and the duration sentence is measured to the check
 *    rather than to now for the same reason.
 *
 * The clock is injected. These labels are ages, and a module that read
 * the clock itself would give a suite that passes at noon and fails at
 * midnight, which teaches a team to re-run tests rather than read them.
 */

import type { PushTone } from './qboPushStatus';

export interface VoidCopyStatusCopy {
  label: string;
  detail: string;
  tone: PushTone;
}

/** The subset of the list row this module reads. */
export interface VoidCopyRow {
  status?: string;
  qbo_invoice_id?: string | null;
  qbo_copy_state?: string | null;
  qbo_copy_of_id?: string | null;
  qbo_copy_checked_at?: string | null;
  qbo_copy_open_since?: string | null;
  qbo_copy_acknowledged?: boolean;
  /** The no-answer copy search (0214), server-computed: null when the
   *  invoice is not one the search is for (not void, a push landed, or no
   *  attempt went unanswered); 'pending' until searched or while it is
   *  too soon to rule a copy out; 'unavailable' when the search could not
   *  settle it; 'not_found' when it found no open copy. */
  qbo_copy_lookup?: 'pending' | 'unavailable' | 'not_found' | null;
}

/** After a week, "could not check" has decayed into "nobody knows". */
const STALE_UNAVAILABLE_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** How old an observation is, decided ONCE. The two renderers below
 *  differ only in wording, so the boundaries live here and cannot drift
 *  apart: a chip reading '7d' beside a sentence reading '1 week ago'
 *  would be two answers to one question.
 *
 *  Clock skew between the app server and Postgres can date an
 *  observation in the future. '-1d' would be nonsense on a money
 *  surface, so anything under 90 seconds reads as the present. */
type Age =
  | { unit: 'now' }
  | { unit: 'minute' | 'hour' | 'day'; n: number }
  | { unit: 'date' };

function ageOf(then: Date, now: Date): Age {
  const ms = now.getTime() - then.getTime();
  if (ms < 90_000) return { unit: 'now' };
  if (ms < 60 * 60_000) return { unit: 'minute', n: Math.floor(ms / 60_000) };
  if (ms < DAY_MS) return { unit: 'hour', n: Math.floor(ms / (60 * 60_000)) };
  if (ms < 8 * DAY_MS) return { unit: 'day', n: Math.floor(ms / DAY_MS) };
  return { unit: 'date' };
}

/** The chip suffix: short enough for a table cell. */
function ageChip(then: Date, now: Date): string {
  const age = ageOf(then, now);
  switch (age.unit) {
    case 'now':
      return 'now';
    case 'minute':
      return `${age.n}m`;
    case 'hour':
      return `${age.n}h`;
    case 'day':
      return `${age.n}d`;
    case 'date':
      return `${then.getDate()} ${MONTHS[then.getMonth()]!.slice(0, 3)}`;
  }
}

/** The same age as a sentence fragment. Deliberately BARE: the two
 *  templates that need the preposition carry their own "on", so no
 *  combination of buckets can ever produce "As of on 12 August". */
function agePhrase(then: Date, now: Date): string {
  const age = ageOf(then, now);
  switch (age.unit) {
    case 'now':
      return 'just now';
    case 'minute':
      return `${plural(age.n, 'minute')} ago`;
    case 'hour':
      return `${plural(age.n, 'hour')} ago`;
    case 'day':
      return `${plural(age.n, 'day')} ago`;
    case 'date':
      return calendarDate(then);
  }
}

function calendarDate(d: Date): string {
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

function parse(iso: string | null | undefined): Date | null {
  if (typeof iso !== 'string' || iso === '') return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * How long the copy had ALREADY been open when Olive last looked.
 *
 * Measured to checked_at, never to now. Rendering now - open_since would
 * assert a present-tense fact about a copy last seen days ago, sitting
 * right beside a sentence that carefully says "as of". Below a day it is
 * not worth a sentence: the as-of already carries it.
 */
function openDuration(openSince: Date | null, checkedAt: Date): string {
  if (openSince === null) return '';
  const ms = checkedAt.getTime() - openSince.getTime();
  if (ms < DAY_MS) return '';
  return ` Had been open for ${plural(Math.floor(ms / DAY_MS), 'day')} when Olive last looked.`;
}

const NEVER_CHECKED: VoidCopyStatusCopy = {
  label: 'Not checked',
  detail:
    "This invoice was voided here, but its QuickBooks copy hasn't been checked yet. Open the invoice to check.",
  tone: 'unknown',
};

/**
 * A stored fact is only a fact about the copy it looked at. When the
 * observation names a different QuickBooks document from the one this
 * invoice actually pushed, it is discarded rather than shown: a fact
 * about copy A decorating copy B is how a surface says "nothing to do"
 * about something nobody has ever examined.
 */
function factIsAboutThisCopy(row: VoidCopyRow): boolean {
  const of = row.qbo_copy_of_id;
  return of == null || of === row.qbo_invoice_id;
}

export function voidCopyStatusCopy(row: VoidCopyRow, now: Date): VoidCopyStatusCopy {
  if (row.qbo_invoice_id == null && (row.qbo_copy_lookup === 'pending' || row.qbo_copy_lookup === 'unavailable')) {
    // The send got no answer, so a copy may be in QuickBooks with no id
    // Olive holds. Same register as a copy Olive could not check.
    return row.qbo_copy_lookup === 'pending'
      ? {
          label: 'Sent, no answer · checking QuickBooks',
          detail:
            "The last send got no answer, so a copy may be in QuickBooks. It's being checked now, and you can open the invoice to check again.",
          tone: 'unknown',
        }
      : {
          label: 'Sent, no answer · could not check',
          detail:
            "The last send got no answer, and QuickBooks couldn't be searched for a copy. Look for this invoice number in QuickBooks.",
          tone: 'blocked',
        };
  }
  if (row.qbo_invoice_id == null && row.qbo_copy_lookup === 'not_found') {
    return {
      label: 'No copy in QuickBooks',
      detail: 'The send of this invoice got no answer. Olive searched QuickBooks and found no open copy.',
      tone: 'quiet',
    };
  }
  if (row.qbo_invoice_id == null) {
    return {
      label: '–',
      detail: 'This invoice never went to QuickBooks, so there is nothing to close there.',
      tone: 'none',
    };
  }

  const checkedAt = parse(row.qbo_copy_checked_at);
  if (row.qbo_copy_state == null || checkedAt === null || !factIsAboutThisCopy(row)) {
    return NEVER_CHECKED;
  }

  const when = agePhrase(checkedAt, now);

  switch (row.qbo_copy_state) {
    case 'open':
      // Danger, not blocked: blocked means nothing was sent and a person
      // can go and fix it. This is a document Olive has cancelled that is
      // still live in QuickBooks and can charge a family. Same hazard as
      // open_with_payment, one payment earlier, so it carries the same
      // register.
      return {
        label: `Still open in QuickBooks · ${ageChip(checkedAt, now)}`,
        detail:
          `Voided here, but as of ${when} the QuickBooks copy was still open and could take a payment. Void the copy in QuickBooks.` +
          openDuration(parse(row.qbo_copy_open_since), checkedAt),
        tone: 'urgent',
      };

    case 'open_with_payment':
      // The loudest state on this surface, and the only one where doing
      // the obvious thing costs money: QuickBooks voids a paid invoice
      // without protest and silently unlinks the payment, leaving the
      // whole amount as unapplied cash on the customer. There is no API
      // guard for that. This sentence is the guard.
      return {
        label: `Open, payment applied · ${ageChip(checkedAt, now)}`,
        detail:
          `Voided here, but as of ${when} the QuickBooks copy had a payment on it. In QuickBooks, unapply the payment and then void the copy, so the money isn't left hanging.` +
          openDuration(parse(row.qbo_copy_open_since), checkedAt),
        tone: 'urgent',
      };

    case 'already_void':
      return {
        label: `Closed in QuickBooks · ${ageChip(checkedAt, now)}`,
        detail: `Voided here, and the QuickBooks copy was void too when Olive last looked, ${when}. Nothing to do.`,
        tone: 'quiet',
      };

    case 'missing_in_qbo':
      if (row.qbo_copy_acknowledged === true) {
        return {
          label: 'Removal confirmed',
          detail: 'Confirmed as deliberately removed from QuickBooks.',
          tone: 'quiet',
        };
      }
      return {
        label: `Not found in QuickBooks · ${ageChip(checkedAt, now)}`,
        detail: `This invoice was sent to QuickBooks, but as of ${when} the copy wasn't there. Check in QuickBooks that it was removed on purpose, then confirm it here.`,
        tone: 'blocked',
      };

    case 'check_unavailable': {
      // ONLY THE WORDING DECAYS. After a week "we tried two days ago" has
      // stopped being useful, so the label collapses to the one an
      // unchecked row shows — the operator act is identical — but the
      // sentence must not, because Olive DID look. Claiming otherwise
      // would be the single untrue statement on this surface. The date
      // is spelled out rather than aged, because "on 9 days ago" is not
      // a sentence.
      if (now.getTime() - checkedAt.getTime() >= STALE_UNAVAILABLE_MS) {
        return {
          label: 'Not checked',
          detail: `The QuickBooks copy couldn't be read when last checked on ${calendarDate(checkedAt)}. Reconnect QuickBooks, then open this invoice again.`,
          tone: 'unknown',
        };
      }
      return {
        label: `Could not check · ${ageChip(checkedAt, now)}`,
        detail: `The QuickBooks copy couldn't be read ${when}. Reconnect QuickBooks, then open this invoice again.`,
        tone: 'blocked',
      };
    }

    default:
      // A state this build does not know is still an answer Olive is
      // holding — a rollback, a half-applied migration, a newer server
      // talking to an older page. It must never read as an all-clear,
      // and it must still name a place to go. The raw token is a wire
      // fact for the log, never operator copy.
      return {
        label: 'Needs a look in QuickBooks',
        detail:
          "This page can't show where the QuickBooks copy stands. Check this invoice in QuickBooks, or reload the page.",
        tone: 'unknown',
      };
  }
}

/** True when this VOID row needs a human before the month can close. */
export function voidCopyNeedsAttention(row: VoidCopyRow, _now: Date): boolean {
  if (row.status !== 'void') return false;
  // Answerable from Olive's own rows: nothing went over, nothing to close.
  if (row.qbo_invoice_id == null) return false;
  // A fact about another copy is not a fact about this one, so this row
  // is still unexamined.
  if (!factIsAboutThisCopy(row)) return true;
  if (row.qbo_copy_state == null) return true;

  switch (row.qbo_copy_state) {
    case 'already_void':
      return false;
    case 'missing_in_qbo':
      // The one state a signature settles. It is deliberately not
      // honoured for any other: a signature about a vanished copy must
      // never silence a copy Olive has since SEEN open.
      return row.qbo_copy_acknowledged !== true;
    default:
      // open, open_with_payment, check_unavailable, and anything this
      // build does not recognise. Stale or fresh, the question is open.
      return true;
  }
}

/**
 * The rows the cockpit banner's payment sentence counts: a void row
 * whose OWN copy was last seen open with a payment applied. Voiding that
 * copy in QuickBooks without unapplying the payment first leaves the
 * money as unapplied cash, which is why the banner spends a sentence on
 * it. Same gate as the cell, stated once: a fact about another copy, or
 * a fact with no readable as-of, is not shown on the row and is not
 * counted here either.
 */
export function voidCopyPaymentApplied(row: VoidCopyRow): boolean {
  if (row.status !== 'void') return false;
  if (row.qbo_invoice_id == null) return false;
  if (row.qbo_copy_state !== 'open_with_payment') return false;
  if (!factIsAboutThisCopy(row)) return false;
  return parse(row.qbo_copy_checked_at) !== null;
}

/**
 * "To send from Olive" — work that finishes on this screen.
 *
 * KEYED ON THE STICKY qbo_invoice_id, NEVER ON qbo_push_status. That
 * column is the LATEST attempt's status, so a push that landed followed
 * by a failed retry reads 'failed' — and this label is an imperative. It
 * would tell an operator to send an invoice QuickBooks already holds,
 * and a second send creates a duplicate. The server shipped and fixed
 * this exact bug once already; it does not get to come back through the
 * screen.
 */
export function rowNeedsSending(row: { status?: string; qbo_invoice_id?: string | null }): boolean {
  return row.status === 'issued' && row.qbo_invoice_id == null;
}

/**
 * "To fix in QuickBooks" — work that cannot be finished from this screen.
 *
 * The void copy check and nothing else. total_mismatch is deliberately
 * absent (founder ruling 2026-08-28): nothing in the system clears it, so
 * a count containing it could never reach zero, and a counter that never
 * reaches zero is what teaches people to stop reading counters. It stays
 * the loudest cell on its own row instead.
 */
export function rowNeedsQboFix(row: VoidCopyRow, now: Date): boolean {
  // The second arm (0214), the twin of needsQboFixSql's: a void with no
  // landed push whose unanswered send may have left a copy, until the
  // search rules it out. 'not_found' clears it; a found copy has become
  // the sticky id and the first arm.
  if (
    row.status === 'void' &&
    row.qbo_invoice_id == null &&
    (row.qbo_copy_lookup === 'pending' || row.qbo_copy_lookup === 'unavailable')
  ) {
    return true;
  }
  return voidCopyNeedsAttention(row, now);
}

/**
 * "Totals Olive couldn't confirm" — the THIRD pile (founder ruling
 * 2026-08-31).
 *
 * An issued invoice pushed with `total_unverifiable` is at Intuit (the
 * sticky id is set), so it is not "to send" — a second send duplicates
 * it. And it is not void, so it is not "to fix in QuickBooks", which is
 * the void copy check and nothing else. Until this ruling the row sat in
 * NO pile: both counts could read zero over a live charge whose total
 * nobody ever compared. This predicate is its pile.
 *
 * STICKY-ID KEYED like both siblings: qbo_push_status is the LATEST
 * attempt's status and is never consulted. qbo_last_error is also the
 * latest attempt's, which is exactly what the operator's cell renders,
 * so the count and the column always tell one story.
 *
 * total_mismatch is DELIBERATELY excluded (founder ruling 2026-08-28,
 * pinned at tests/integration/114-invoice-list-month-filter.test.ts
 * "total_mismatch never joins a count on its own" and in
 * tests/ui/Invoices.test.tsx): nothing in the tree clears it, and a
 * counter that cannot reach zero teaches the office to stop reading
 * counters. It stays the loudest cell on its own row.
 *
 * The SQL twin is needsUnconfirmedSql (api/_shared/repos/invoices.ts);
 * the two must agree on every wire row — the twins discipline pinned in
 * integration test 114.
 */
export function rowNeedsUnconfirmed(row: {
  status?: string;
  qbo_invoice_id?: string | null;
  qbo_last_error?: string | null;
}): boolean {
  return (
    row.status === 'issued' &&
    row.qbo_invoice_id != null &&
    row.qbo_last_error === 'total_unverifiable'
  );
}
